import React, { useState, useEffect } from 'react';
import { Form, Button, Container, Row, Col } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { API_MASTER_URL } from '../../config/config';

const MasterSuppressionSetting = () => {
    const [clientId, setClientId] = useState('');
    const [listOptions, setListOptions] = useState([]);
    const [segmentOptions, setSegmentOptions] = useState([]);
    const [segmentIds, setSegmentIds] = useState([]);
    const [masterSuppressionList, setMasterSuppressionList] = useState('');
    const [suppressionListOptions, setSuppressionListOptions] = useState([]);
    const [error, setError] = useState('');
    const [insertedData, setInsertedData] = useState([]);
    const [editingIndex, setEditingIndex] = useState(-1);
    const [serverMessage, setServerMessage] = useState('');
    const [submitButtonText, setSubmitButtonText] = useState('Submit');

    useEffect(() => {
        const fetchListOptions = async (type, setter) => {
            try {
                let payload = { type: type };
                if (type === 'suppression') {
                    payload.name_like = 'Master Suppression';
                }
                const response = await axios.post(`${API_MASTER_URL}/get-lists`, payload);
                const lists = response.data.payload.filter(list => list.account_id === parseInt(clientId));
                if (lists.length > 0) {
                    const sortedLists = lists.sort((a, b) => a.name.localeCompare(b.name));
                    const options = sortedLists.map(list => ({
                        value: list.id,
                        label: `${list.name} (${list.id})`
                    }));
                    setter(options);
                    setError('');
                } else {
                    setter([]);
                    setError(`No ${type} available`);
                }
            } catch (error) {
                console.error(`Error fetching ${type}:`, error);
                setter([]);
                setError(`Error fetching ${type}`);
            }
        };

        if (clientId) {
            fetchListOptions('sending', setListOptions);
            fetchListOptions('suppression', setSuppressionListOptions);
        } else {
            setListOptions([]);
            setSuppressionListOptions([]);
            setError('');
        }

        fetchMasterTableData();
    }, [clientId]);

    const fetchMasterTableData = async () => {
        try {
            const response = await axios.get(`${API_MASTER_URL}/get-master-data-list`);
            setInsertedData(response.data.payload);
            setError('');
        } catch (error) {
            console.error('Error fetching master table data:', error);
            setInsertedData([]);
            setError('Error fetching master table data');
        }
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        if (editingIndex === -1) {
            const listId = listOptions.find(option => option.isSelected)?.value || '';
            const selectedSegmentIds = segmentIds.map(option => option.value);
            const suppressionId = masterSuppressionList?.value || '';

            setEditingIndex(-1);

            try {
                const response = await axios.post(`${API_MASTER_URL}/add-master-lists`, {
                    clientId,
                    listId,
                    segmentIds: selectedSegmentIds,
                    masterSuppressionList: suppressionId
                });
                console.log('Item inserted:', response.data);
                
                setServerMessage(response.data.message);
                setSubmitButtonText('Submit');

                resetForm();
                fetchMasterTableData();
            } catch (error) {
                setError(`Error submitting form: ${error}`);
                console.error('Error submitting form:', error);
                setSubmitButtonText('Submit');
            }
        } else {
            handleSave(editingIndex);
        }
    };

    const changeButtonText = (text) => {
        setSubmitButtonText(text);
    };

    const setSelectedListOptions = (listId) => {
        const selectedListOption = listOptions.find(option => option.value === listId);
        if (selectedListOption) {
            handleListIdChange(selectedListOption);
        }
        return selectedListOption;
    };   

    const setSelectedSegmentOptions = (segmentIds) => {
        let selectedSegmentIds;

        if (typeof segmentIds === 'string') {
            selectedSegmentIds = segmentIds.split(',').map(id => id.trim());
        } else if (Array.isArray(segmentIds)) {
            selectedSegmentIds = segmentIds.map(id => id.toString().trim());
        } else {
            selectedSegmentIds = [segmentIds.toString().trim()];
        }

        const selectedSegmentOptions = selectedSegmentIds.map(id => segmentOptions.find(option => option.value === id));
        setSegmentIds(selectedSegmentOptions);
    };

    const handleEdit = (index) => {
        const editItem = insertedData[index];
        setEditingIndex(index);
        setClientId(editItem.clientId); 
        setSelectedListOptions(editItem.listId);
        setSelectedSegmentOptions(editItem.segmentIds);
        setMasterSuppressionList({ value: editItem.masterSuppressionList, label: `${editItem.masterSuppressionList} (label if any)` });
        changeButtonText('Update');
    };    

    const handleSave = async (index) => {
        try {
            const updatedItem = insertedData[index];

            const listId = listOptions.find(option => option.isSelected)?.value || '';
            const selectedSegmentIds = segmentIds.map(option => option.value);
            const suppressionId = masterSuppressionList?.value || '';

            const response = await axios.put(`${API_MASTER_URL}/update-master-list-data/${updatedItem.id}`, {
                clientId,
                listId: listId,
                segmentIds: selectedSegmentIds,
                masterSuppressionList: suppressionId
            });
            console.log('Item updated:', response.data);
            setEditingIndex(-1); 

            setServerMessage(response.data.message);
            setSubmitButtonText('Submit');
            
            fetchMasterTableData();
            resetForm();
        } catch (error) {
            console.error('Error updating item:', error);
            setError(`Error updating form: ${error}`);
        }
    };

    const handleClientIdChange = async (e) => {
        const clientId = e.target.value;
        setClientId(clientId);
    };

    const handleListIdChange = async (selectedOption) => {
        const listId = selectedOption.value;
        const selectedListOptions = listOptions.map(option => ({
            ...option,
            isSelected: option.value === listId
        }));
        setListOptions(selectedListOptions);
        setSegmentIds([]);
        if (listId) {
            try {
                const response = await axios.post(`${API_MASTER_URL}/get-segments`, { list_id: listId });
                const segments = response.data.payload;
                if (segments.length > 0) {
                    const sortedSegments = segments.sort((a, b) => a.name.localeCompare(b.name));
                    setSegmentOptions(sortedSegments.map(segment => ({ value: segment.id, label: `${segment.name} (${segment.id})` })));
                    setError('');
                } else {
                    setSegmentOptions([]);
                    setError('No segment ID available');
                }
            } catch (error) {
                console.error('Error fetching segment IDs:', error);
                setSegmentOptions([]);
                setError('Error fetching segment IDs');
            }
        } else {
            setSegmentOptions([]);
            setError('');
        }
    };

    const resetForm = () => {
        setEditingIndex(-1);
        setClientId('');
        setListOptions([]);
        setSegmentIds([]);
        setMasterSuppressionList('');
        setSuppressionListOptions([]);
        changeButtonText('Submit');
    };

    const handleStatusChange = async (index, currentStatus) => {
        const updatedData = [...insertedData];
        updatedData[index].status = currentStatus === true ? false : true;
        setInsertedData(updatedData);
    
        const confirmation = window.confirm(`Are you sure you want to change the status to ${updatedData[index].status === true ? 'Active' : 'Inactive'}?`);
        
        if (confirmation) {
            try {
                const response = await axios.put(
                    `${API_MASTER_URL}/change-master-list-status/${updatedData[index].id}`,
                    { newStatus: updatedData[index].status }
                );
                setServerMessage(`'Status updated: ${response.data.message}`);
                console.log('Status updated:', response.data);
            } catch (error) {
                console.error('Error updating status:', error);
                setError(`Error updating status: ${error}`);
            }
        } else {
            updatedData[index].status = currentStatus;
            setInsertedData(updatedData);
        }
    };    
    
    const handleDelete = async (index) => {
        const itemToDelete = insertedData[index];
        
        const confirmation = window.confirm(`Are you sure you want to delete item ${itemToDelete.listId}?`);
        
        if (confirmation) {
            try {
                const response = await axios.delete(`${API_MASTER_URL}/delete-masters-list-data/${itemToDelete.id}`);
                console.log('Item deleted:', response.data);
                const updatedData = [...insertedData];
                updatedData.splice(index, 1);
                setInsertedData(updatedData);
                setServerMessage(`'Item deleted: ${response.data.message}`);
            } catch (error) {
                console.error('Error deleting item:', error);
                setError(`Error deleting item: ${error}`);
            }
        }
    };

    const handleStartProcess = async (listId, isActive) => {
        if (!isActive) return;

        try {
            const response = await axios.get(`${API_MASTER_URL}/master-export-file/${listId}`);
            console.log('Start Process response:', response.data);
            setServerMessage(`Start Process response: ${response.data}`);
        } catch (error) {
            console.error('Error calling process start API:', error);
        }
    };

    const headers = [
        'No',
        'Account ID',
        'List ID',
        'Segment ID',
        'Master Suppression ID',
        'StartProcess/Edit',
        'Status',
        'Delete'
    ];

    const renderTable = () => {
        return (
            <table className="table mt-4">
                <thead>
                    <tr>
                        {headers.map((header, index) => (
                            <th key={index}>{header}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {insertedData.map((data, index) => (
                        <tr key={index}>
                            <td>{index + 1}</td>
                            <td>{data.clientId}</td>
                            <td>{data.listId}</td>
                            <td>{Array.isArray(data.segmentIds) ? data.segmentIds.join(', ') : data.segmentIds}</td>
                            <td>{data.masterSuppressionList}</td>
                            <td>
                                <Button 
                                    variant="primary" 
                                    onClick={() => handleStartProcess(data.listId, data.status)}
                                    disabled={!data.status}
                                >
                                    Start
                                </Button>&nbsp;
                                <Button variant="secondary" onClick={() => handleEdit(index)}>
                                    Edit
                                </Button>
                            </td>
                            <td>
                                <Button
                                    variant={data.status === true ? 'success' : 'danger'}
                                    onClick={() => handleStatusChange(index, data.status)}
                                >
                                    {data.status === true ? 'Active' : 'Inactive'}
                                </Button>
                            </td>
                            <td>
                                <Button variant="danger" onClick={() => handleDelete(index)}>
                                    Delete
                                </Button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    };

    return (
        <Container>
    <Form onSubmit={handleFormSubmit}>
        <Row>
            <Col md={6}>
                <Form.Group controlId="clientId">
                    <Form.Control
                        type="text"
                        value={clientId}
                        onChange={handleClientIdChange}
                        placeholder="Enter Client ID"
                    />
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group controlId="listId">
                    <Select
                        value={listOptions.find(option => option.isSelected) || null}
                        onChange={handleListIdChange}
                        options={listOptions}
                        placeholder="Select List IDs"
                        isDisabled={!clientId || error}
                    />
                </Form.Group>
            </Col>
        </Row>
        <Row className='mt-3 align-items-center'>
            <Col md={6}>
                <Form.Group controlId="segmentIds">
                    <Select
                        isMulti
                        value={segmentIds}
                        onChange={(selectedOptions) => setSegmentIds(selectedOptions)}
                        options={segmentOptions}
                        placeholder="Select Segment IDs"
                        isDisabled={!clientId || error}
                    />
                </Form.Group>
            </Col>
            <Col md={6}>
                <Form.Group controlId="masterSuppressionList">
                    <Select
                        value={suppressionListOptions.find(option => option.value === masterSuppressionList?.value)}
                        onChange={(selectedOption) => setMasterSuppressionList(selectedOption)}
                        options={suppressionListOptions}
                        placeholder="Select Suppression List"
                        isDisabled={!clientId || error}
                    />
                </Form.Group>
            </Col>
            <Col md={12} className="text-center mt-3">
                <Button id="submitButton" variant="primary" type="submit" size="sm" className='me-2'>
                    {submitButtonText}
                </Button>
                <Button id="resetButton" variant="danger" type="button" size="sm" onClick={() => resetForm()}>
                    Reset
                </Button>
            </Col>
            
        </Row>
    </Form>
    {serverMessage && <p>{serverMessage}</p>}
    {insertedData.length > 0 && renderTable()}
</Container>
    );
};

export default MasterSuppressionSetting;
